// Copyright (c) 2025-2026 NacenCheck — TracePro Compliance-Grade Trace OS.
// SPDX-License-Identifier: MIT

"use client";

/**
 * TanStack Query provider — app-wide request layer. Adopted to give every data
 * fetch automatic in-flight de-duplication by queryKey: when React mounts a
 * component twice during the initial concurrent render (mount → discard →
 * remount), both subscribers share ONE network request instead of firing a
 * redundant pair (the supply-chain section's 2× roles+list, one of which could
 * race a token refresh into a 401). New call sites should prefer useQuery/
 * useMutation over ad-hoc useEffect fetches.
 */

import React, { useState } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

export function QueryProvider({ children }: { children: React.ReactNode }) {
  // One client per browser session (useState initializer — never re-created).
  const [client] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Treat data fresh for 30s → a remount within the window reuses cache
            // (no refetch); concurrent identical keys always share one request.
            staleTime: 30_000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
        },
      })
  );
  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
